import { useActiveCircle } from '@/hooks/use-active-circle';
import { usePartnerById } from '@/hooks/use-partner';
import {
    AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE,
    AmplitudeExperimentClient,
    initAmplitudeExperimentClient,
    setupAmplitudeExperimentUser,
} from '@/libs/feature-flags';
import { ExperimentUser } from '@amplitude/experiment-js-client';
import useAccount from '@rhg/hooks/use-account';
import useAppConfig from '@rhg/hooks/use-app-config';
import { useCurrentLanguage } from '@rhg/hooks/use-current-language';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import { ExperimentUserProps } from '@rhg/providers/experiment-client/types';
import isEqual from 'lodash/isEqual';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

export type { ExperimentUserProps } from './types';

export type AmplitudeExperimentContext = {
    experimentClient: AmplitudeExperimentClient;
    setAmplitudeExperimentUser: (
        experimentUserProps: ExperimentUserProps
    ) => Promise<void>;
    experimentUser?: ExperimentUser;
    // `null` signals "no ExperimentUserSyncProvider in the tree" — distinguishable
    // from a synced-but-not-ready `false`. See useExperimentContext for the dev warn.
    isExperimentUserReady: boolean | null;
};

const defaultState: AmplitudeExperimentContext = {
    experimentClient:
        AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE as AmplitudeExperimentClient,
    setAmplitudeExperimentUser: async () => {},
    isExperimentUserReady: null,
};

export const ExperimentContext =
    createContext<AmplitudeExperimentContext>(defaultState);

/**
 * Provider for managing experiments and feature flags using Amplitude's Experiment SDK.
 *
 * Mounts above the auth / active-circle / query-client providers, so it only
 * handles SDK initialization and exposes a setAmplitudeExperimentUser mutator.
 * Partner-aware syncing happens in {@link ExperimentUserSyncProvider}, which
 * must be mounted below those providers.
 */
export const ExperimentsProvider = ({ children }: PropsWithChildren) => {
    const [experimentClient, setExperimentClient] =
        useState<AmplitudeExperimentClient>('client-unavailable');
    const [experimentUser, setExperimentUser] = useState<ExperimentUser>();
    const isExperimentClientInitialized = useRef<boolean>(false);
    const pendingNewExperimentUser = useRef<ExperimentUser | undefined>(
        undefined
    );
    const experimentUserRef = useRef<ExperimentUser | undefined>(undefined);

    useEffect(() => {
        experimentUserRef.current = experimentUser;
    }, [experimentUser]);
    const {
        NEXT_PUBLIC_FF_EXPERIMENT_PROXY_ENABLED: experimentProxyEnabled,
        NEXT_PUBLIC_AMPLITUDE_CH_EVENTS_PROXY_URL: chEventsProxyUrl,
    } = useAppConfig();

    useEffect(() => {
        const initializeExperimentClient = async () => {
            try {
                const amplitudeExperimentClient =
                    await initAmplitudeExperimentClient({
                        experimentProxyEnabled,
                        chEventsProxyUrl,
                    });
                isExperimentClientInitialized.current = true;
                setExperimentClient(amplitudeExperimentClient);
            } catch (error) {
                console.error('Error initializing ExperimentClient:', error);
                setExperimentClient(AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE);
            }
        };

        if (!isExperimentClientInitialized.current) {
            void initializeExperimentClient();
        }
    }, [experimentProxyEnabled, chEventsProxyUrl]);

    const setAmplitudeExperimentUser = useCallback(
        async (experimentUserProps: ExperimentUserProps) => {
            if (
                experimentClient &&
                experimentClient !== AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE
            ) {
                const newExperimentUser =
                    setupAmplitudeExperimentUser(experimentUserProps);
                // Ensure we do not set the user and send an API request to Amplitude when the user update is already pending or completed
                if (
                    isEqual(newExperimentUser, experimentUserRef.current) ||
                    isEqual(newExperimentUser, pendingNewExperimentUser.current)
                ) {
                    return;
                }
                pendingNewExperimentUser.current = newExperimentUser;
                try {
                    await experimentClient.fetch(newExperimentUser);
                    // Update experiment user to force dependent hooks to re-evaluate.
                    // experimentClient reference does not change after initialization
                    setExperimentUser(newExperimentUser);
                } finally {
                    pendingNewExperimentUser.current = undefined;
                }
            }
        },
        [experimentClient]
    );

    const value = useMemo<AmplitudeExperimentContext>(
        () => ({
            experimentClient,
            experimentUser,
            setAmplitudeExperimentUser,
            isExperimentUserReady: null,
        }),
        [experimentClient, setAmplitudeExperimentUser, experimentUser]
    );

    return (
        <ExperimentContext.Provider value={value}>
            {children}
        </ExperimentContext.Provider>
    );
};

/**
 * Drives the experiment user from the active circle + partner config and
 * exposes `isExperimentUserReady` so consumers know when it is safe to
 * evaluate flags. Must be mounted below {@link ExperimentsProvider} and below
 * the auth / active-circle / query-client providers it depends on.
 */
export const ExperimentUserSyncProvider = ({ children }: PropsWithChildren) => {
    const { experimentClient, experimentUser, setAmplitudeExperimentUser } =
        useContext(ExperimentContext);
    const { isLoading: isAuthLoading, isAuthenticated } = useAuth();
    const {
        activeCircle,
        globalClassifierIdentifiers,
        isLoading: isActiveCircleLoading,
    } = useActiveCircle();
    const { partner, isLoading: isPartnerLoading } = usePartnerById(
        activeCircle?.sponsor?.partnerId
    );
    const { account, isLoading: isAccountLoading } = useAccount();
    const language = useCurrentLanguage();
    const [failedExperimentUserProps, setFailedExperimentUserProps] = useState<
        ExperimentUserProps | undefined
    >();

    /**
     * The sole assembly point for the experiment user's inputs. Both the sync
     * effect and the readiness check derive from this one object, so adding a
     * property to {@link ExperimentUserProps} means editing only here (plus
     * `setupAmplitudeExperimentUser`, which maps props onto the Amplitude user).
     */
    const experimentUserProps = useMemo<ExperimentUserProps>(
        () => ({
            partnerId: activeCircle?.sponsor?.partnerId,
            country: globalClassifierIdentifiers?.countryCodeClassifier,
            language,
            isDependent: activeCircle?.sponsor?.isDependentGroup,
            isConciergeEnabled: partner?.isConciergeEnabled,
            isJournalingEnabled: partner?.isJournalingEnabled,
            dob: account?.dob,
        }),
        [
            activeCircle?.sponsor?.partnerId,
            globalClassifierIdentifiers?.countryCodeClassifier,
            language,
            activeCircle?.sponsor?.isDependentGroup,
            partner?.isConciergeEnabled,
            partner?.isJournalingEnabled,
            account?.dob,
        ]
    );

    useEffect(() => {
        async function syncExperimentUser(): Promise<void> {
            if (
                experimentClient &&
                experimentClient !==
                    AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE &&
                experimentUserProps.partnerId &&
                !isPartnerLoading &&
                !isAccountLoading
            ) {
                // No-ops when the computed user already matches the fetched
                // one — setAmplitudeExperimentUser dedupes before fetching.
                await setAmplitudeExperimentUser(experimentUserProps);
                setFailedExperimentUserProps(undefined);
            }
        }
        syncExperimentUser().catch((error) => {
            setFailedExperimentUserProps(experimentUserProps);
            return logger.error(
                'Error in ExperimentUserSyncProvider syncExperimentUser',
                {},
                error
            );
        });
    }, [
        experimentClient,
        setAmplitudeExperimentUser,
        experimentUserProps,
        isPartnerLoading,
        isAccountLoading,
    ]);

    const isExperimentUserReady = useMemo(() => {
        if (
            !experimentClient ||
            experimentClient === AMPLITUDE_EXPERIMENTS_UNINITIALIZED_STATE ||
            isAuthLoading
        ) {
            return false;
        }
        if (!isAuthenticated) {
            return true;
        }
        // Authenticated path: wait until we know whether the user is sponsored.
        if (isActiveCircleLoading) {
            return false;
        }
        if (!experimentUserProps.partnerId) {
            // Non-sponsored authenticated user — evaluate immediately.
            return true;
        }
        // Sponsored user — these gate the sync effect, so until they settle
        // there is no computed user worth comparing against.
        if (isPartnerLoading || isAccountLoading) {
            return false;
        }

        // The attempt for these inputs failed, so the fetched user will never
        // catch up to them without a change of input. Report ready rather than
        // holding every flag consumer at loading indefinitely.
        if (isEqual(experimentUserProps, failedExperimentUserProps)) {
            return true;
        }

        // Ready exactly when the fetched user matches what the sync effect
        // would send. This is the same comparison setAmplitudeExperimentUser
        // dedupes on, so sync fires when they differ and readiness flips true
        // when they converge — no per-property list to keep in step.
        return isEqual(
            setupAmplitudeExperimentUser(experimentUserProps),
            experimentUser
        );
    }, [
        experimentClient,
        isAuthLoading,
        isAuthenticated,
        isActiveCircleLoading,
        isPartnerLoading,
        isAccountLoading,
        experimentUserProps,
        experimentUser,
        failedExperimentUserProps,
    ]);

    const value = useMemo<AmplitudeExperimentContext>(
        () => ({
            experimentClient,
            experimentUser,
            setAmplitudeExperimentUser,
            isExperimentUserReady,
        }),
        [
            experimentClient,
            experimentUser,
            setAmplitudeExperimentUser,
            isExperimentUserReady,
        ]
    );

    return (
        <ExperimentContext.Provider value={value}>
            {children}
        </ExperimentContext.Provider>
    );
};
